import React from 'react'
import { CopySuggestion } from '@marketing-milk/interfaces'

type Props = {
  previewHtml: string
  suggestion: CopySuggestion
  onImport: () => void
  onClose: () => void
}

function extractIframeSrc(html: string): string | null {
  const match = html.match(/src="([^"]+)"/)
  return match ? match[1].replace(/&amp;/g, '&') : null
}

export function CopyPreviewModal({ previewHtml, suggestion, onImport, onClose }: Props) {
  const iframeSrc = extractIframeSrc(previewHtml)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl flex flex-col"
        style={{ maxHeight: '95vh' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg font-bold text-gray-900">Ad Preview</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <i className="fa fa-times text-lg" />
          </button>
        </div>

        {/* Body — two columns */}
        <div className="flex gap-6 p-6 flex-1 min-h-0">
          {/* Left: FB preview — own iframe with tall fixed height */}
          <div className="flex-shrink-0">
            {iframeSrc ? (
              <iframe
                src={iframeSrc}
                width={340}
                height={700}
                scrolling="no"
                style={{ border: 'none', display: 'block' }}
              />
            ) : (
              <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
            )}
          </div>

          {/* Right: copy summary */}
          <div className="flex-1 min-w-0 flex flex-col gap-4 overflow-y-auto">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Selected Copy
            </p>
            {suggestion.headline && <CopyRow label="Headline" value={suggestion.headline} />}
            {suggestion.description && (
              <CopyRow label="Description" value={suggestion.description} />
            )}
            {suggestion.graphicText && (
              <CopyRow label="Graphic Text" value={suggestion.graphicText} />
            )}
            {suggestion.primaryText && (
              <CopyRow label="Primary Text" value={suggestion.primaryText} />
            )}
            <div className="flex flex-col gap-1">
              <span className="text-xs text-gray-500">CTA</span>
              <span className="font-mono bg-gray-100 px-2 py-1 rounded text-xs text-gray-700 self-start">
                {suggestion.callToAction}
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-6 py-4 border-t border-gray-200 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Back
          </button>
          <button
            type="button"
            onClick={onImport}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl bg-purple-600 text-white font-semibold text-sm hover:bg-purple-700 transition-colors shadow-md"
          >
            <i className="fa fa-file-import" />
            Import with This Copy
          </button>
        </div>
      </div>
    </div>
  )
}

function CopyRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-gray-500">{label}</span>
      <span className="text-sm text-gray-800">{value}</span>
    </div>
  )
}
